import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import GovContractingPage from "@/pages/GovContractingPage";
import ContactPage from "@/pages/ContactPage";
import CareersPage from "@/pages/CareersPage";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ContentEditor from "@/pages/admin/ContentEditor";
import JobsManager from "@/pages/admin/JobsManager";
import ApplicationsManager from "@/pages/admin/ApplicationsManager";
import SeoEditor from "@/pages/admin/SeoEditor";
import SettingsEditor from "@/pages/admin/SettingsEditor";
import DocumentsManager from "@/pages/admin/DocumentsManager";
import UsersManager from "@/pages/admin/UsersManager";

const PAGE_TITLES = {};

function ScrollToTopAndTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#121212]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      {children}
      <Footer />
    </div>
  );
}

function AdminGate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-[#888] text-sm">Loading…</div>
      </div>
    );
  }
  if (!user) return <AdminLogin />;
  return <AdminLayout />;
}

function RequireSuperAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/admin" replace />;
  if (user.role !== 'superadmin') return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTopAndTitle />
          <Routes>
          {/* Admin (no public navbar/footer) */}
          <Route path="/admin" element={<AdminGate />}>
            <Route index element={<AdminDashboard />} />
            <Route path="content" element={<ContentEditor />} />
            <Route path="jobs" element={<JobsManager />} />
            <Route path="applications" element={<ApplicationsManager />} />
            <Route path="seo" element={<SeoEditor />} />
            <Route path="documents" element={<DocumentsManager />} />
            <Route path="settings" element={<SettingsEditor />} />
            <Route path="users" element={<RequireSuperAdmin><UsersManager /></RequireSuperAdmin>} />
          </Route>

          {/* Public site */}
          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
          <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
          <Route path="/government" element={<PublicLayout><GovContractingPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
          <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
        </Routes>
      </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
